import { ChangeDetectionStrategy, Component, DestroyRef, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { connectState } from '@examdojo/angular/util';
import { OidcProvider } from '@examdojo/auth';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { ErrorMessages } from '@examdojo/core/form';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { ImageCropFileInputComponent } from '@examdojo/core/image-manipulation/crop';
import { ToastService } from '@examdojo/core/toast';
import { ImagePreviewDirective } from '@examdojo/ui/image-preview';
import { TextInputComponent } from '@examdojo/ui/input';
import { getFormSavingStatus } from '@examdojo/ui/save-indicator/get-form-saving-status';
import { EMPTY, filter, finalize, map, startWith, switchMap, tap } from 'rxjs';
import { AuthService, AVATARS_STORAGE_BUCKET_NAME, UserQuery, UserService } from '../index';

const MAX_FILE_SIZE = 500 * 1024; // 500KB

@Component({
  selector: 'dojo-profile',
  imports: [
    ReactiveFormsModule,
    TextInputComponent,
    IconComponent,
    IconButtonComponent,
    IconComponent,
    ButtonComponent,
    ImagePreviewDirective,
    ImageCropFileInputComponent,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      ::ng-deep .dark .apple-logo {
        filter: invert(1);
      }
    `,
  ],
})
export class ProfileComponent {
  constructor(
    private readonly userQuery: UserQuery,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly destroyRef: DestroyRef,
  ) {
    // Only listen to user changes to update forms, no auto-save
    this.updateFormsOnUserChange().pipe(takeUntilDestroyed()).subscribe();

    // Update provider connection status when user changes
    this.authService.currentUser$
      .pipe(
        filter(Boolean),
        tap((user) => {
          const isGoogleProviderConnected = user.identities?.find((identity) => identity.provider === 'google');
          const isDiscordProviderConnected = user.identities?.find((identity) => identity.provider === 'discord');
          const isPasswordProviderConnected = user.identities?.find((identity) => identity.provider === 'email');

          this.isGoogleProviderConnectedFormCtrl.setValue(Boolean(isGoogleProviderConnected));
          this.isDiscordProviderConnectedFormCtrl.setValue(Boolean(isDiscordProviderConnected));
          this.isPasswordProviderConnectedFormCtrl.setValue(Boolean(isPasswordProviderConnected));
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Reset email update pending flag when email changes
    this.userForm.controls.email.valueChanges
      .pipe(
        tap(() => {
          if (this.emailUpdatePending()) {
            this.emailUpdatePending.set(false);
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly userForm = new FormGroup({
    first_name: new FormControl<string>('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    last_name: new FormControl<string>('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    email: new FormControl<string>('', {
      validators: [Validators.required, Validators.email],
      nonNullable: true,
    }),
  });

  readonly passwordForm = new FormGroup(
    {
      newPassword: new FormControl<string>('', {
        validators: [Validators.required, Validators.minLength(6)],
        nonNullable: true,
      }),
      confirmPassword: new FormControl<string>('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
    },
    { validators: this.passwordMatchValidator.bind(this) },
  );

  readonly isGoogleProviderConnectedFormCtrl = new FormControl<boolean>(false, { nonNullable: true });
  readonly isDiscordProviderConnectedFormCtrl = new FormControl<boolean>(false, { nonNullable: true });
  readonly isPasswordProviderConnectedFormCtrl = new FormControl<boolean>(false, { nonNullable: true });

  readonly isGoogleLoadingFormCtrl = new FormControl<boolean>(false, { nonNullable: true });
  readonly isDiscordLoadingFormCtrl = new FormControl<boolean>(false, { nonNullable: true });
  readonly isPasswordLoadingFormCtrl = new FormControl<boolean>(false, { nonNullable: true });

  readonly AVATARS_STORAGE_BUCKET_NAME = AVATARS_STORAGE_BUCKET_NAME;
  readonly fileInput = viewChild<ImageCropFileInputComponent>('fileInput');
  readonly userFormStatus$ = getFormSavingStatus(this.userForm);
  readonly MAX_FILE_SIZE = MAX_FILE_SIZE;
  readonly isSavingUserInfo = signal(false);
  readonly isSavingPassword = signal(false);
  readonly emailUpdatePending = signal(false);

  readonly confirmPasswordErrorMessages: ErrorMessages = {
    mismatch: 'Passwords do not match.',
    required: 'This field is required',
  };

  // Expose OidcProvider enum to template
  readonly OidcProvider = OidcProvider;

  readonly state = connectState({
    user: this.userQuery.active$,
    profilePicUrl: this.userQuery.profilePic$.pipe(startWith(null)),
    oAuthProfilePicUrl: this.userQuery.oAuthProfilePic$,
    authProvider: this.authService.selectAuthProvider(),
    isGoogleProvider: this.authService.selectAuthProvider().pipe(map((provider) => provider === 'google')),
    isAppleProvider: this.authService.selectAuthProvider().pipe(map((provider) => provider === 'apple')),
    isGoogleProviderConnected: this.isGoogleProviderConnectedFormCtrl.valueChanges.pipe(
      startWith(this.isGoogleProviderConnectedFormCtrl.value),
    ),
    isDiscordProviderConnected: this.isDiscordProviderConnectedFormCtrl.valueChanges.pipe(
      startWith(this.isDiscordProviderConnectedFormCtrl.value),
    ),
    isPasswordProviderConnected: this.isPasswordProviderConnectedFormCtrl.valueChanges.pipe(
      startWith(this.isPasswordProviderConnectedFormCtrl.value),
    ),
    isGoogleLoading: this.isGoogleLoadingFormCtrl.valueChanges.pipe(startWith(this.isGoogleLoadingFormCtrl.value)),
    isDiscordLoading: this.isDiscordLoadingFormCtrl.valueChanges.pipe(startWith(this.isDiscordLoadingFormCtrl.value)),
    isPasswordLoading: this.isPasswordLoadingFormCtrl.valueChanges.pipe(
      startWith(this.isPasswordLoadingFormCtrl.value),
    ),
    currentLoginProvider: this.authService.selectAuthProvider(),
  });

  get isPasswordFormValid(): boolean {
    return this.passwordForm.valid;
  }

  updateProfilePicture(url: string) {
    this.userService
      .update(this.userQuery.getActiveId()!, { profile_pic: url })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  triggerProfilePictureUpload() {
    this.fileInput()?.openFilePicker();
  }

  removeProfilePicture() {
    const profilePicture = this.userQuery.getActive()?.profile_pic;

    if (!profilePicture) {
      return;
    }

    this.userService
      .update(this.userQuery.getActiveId()!, { profile_pic: null })
      .pipe(
        switchMap(() => this.userService.deleteProfilePicture(profilePicture)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  saveUserInfo() {
    if (!this.userForm.valid || !this.userForm.dirty || this.isSavingUserInfo()) {
      return;
    }

    const formValue = this.userForm.getRawValue();
    const currentUser = this.state.user;
    const emailChanged = currentUser && formValue.email !== currentUser.email;

    this.isSavingUserInfo.set(true);

    this.authService
      .updateUser({
        data: {
          first_name: formValue.first_name,
          last_name: formValue.last_name,
        },
        email: formValue.email,
      })
      .pipe(
        switchMap(() => this.userService.fetchActiveUserProfile()),
        tap(() => {
          this.userForm.markAsPristine();

          if (emailChanged) {
            this.emailUpdatePending.set(true);
          }

          this.toastService.success('Profile updated successfully');
        }),
        this.errorHandlerService.catchError(
          `[ProfileComponent.saveUserInfo]: Failed to update the users credentials`,
          (error: unknown) => {
            // Check the specific error to provide appropriate feedback
            if ((error as { error_code?: string })?.error_code === 'email_already_used') {
              this.userForm.controls.email.setErrors({ email: 'Already used' });
              this.toastService.error('This email address is already in use.');
            } else {
              // For other errors, show generic message and don't modify form
              this.toastService.error('Failed to update profile. Please try again.');
            }
            return EMPTY;
          },
        ),
        finalize(() => {
          this.isSavingUserInfo.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  changePassword() {
    if (!this.isPasswordFormValid || this.isSavingPassword()) {
      return;
    }

    const { newPassword } = this.passwordForm.getRawValue();
    this.isSavingPassword.set(true);

    this.authService
      .updateUser({ password: newPassword })
      .pipe(
        tap(() => {
          this.passwordForm.reset();
          this.passwordForm.markAsPristine();
          this.toastService.success('Password updated successfully');
        }),
        finalize(() => {
          this.isSavingPassword.set(false);
        }),
        this.errorHandlerService.catchError(
          '[ProfileComponent.changePassword]: Failed to update password',
          (error: unknown) => {
            // Handle specific API error for same password
            const errorObj = error as { error_code?: string; message?: string };
            if (
              errorObj?.error_code === 'same_password' ||
              errorObj?.message?.includes('should be different from the old password')
            ) {
              this.toastService.error('New password should be different from the old password.');
            } else {
              this.toastService.error('An error occurred while updating the password. Please try again.');
            }
            return EMPTY;
          },
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  async onToggleChange(provider: OidcProvider, event: MatSlideToggleChange) {
    // Set loading state based on provider
    const loadingCtrl = this.getLoadingCtrl(provider);
    loadingCtrl.setValue(true);

    try {
      if (event.checked) {
        await this.authService.linkProvider(provider);
      } else {
        await this.authService.unlinkProvider(provider);
      }
    } catch {
      // Handled in the service
    } finally {
      loadingCtrl.setValue(false);
    }
  }

  private getLoadingCtrl(provider: OidcProvider): FormControl<boolean> {
    switch (provider) {
      case OidcProvider.Google:
        return this.isGoogleLoadingFormCtrl;
      case OidcProvider.Discord:
        return this.isDiscordLoadingFormCtrl;
      default:
        return this.isGoogleLoadingFormCtrl;
    }
  }

  private updateFormsOnUserChange() {
    return this.userQuery.active$.pipe(
      filter(Boolean),
      tap((activeUser) => {
        this.userForm.patchValue(
          {
            first_name: activeUser.first_name ?? undefined,
            last_name: activeUser.last_name ?? undefined,
            email: activeUser.email,
          },
          { emitEvent: false },
        );
      }),
    );
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const formGroup = control as FormGroup;
    const newPassword = formGroup.get('newPassword')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;
    const confirmPasswordControl = formGroup.get('confirmPassword');

    if (!newPassword || !confirmPassword) {
      return null; // Let individual field validators handle empty values
    }

    const passwordsMatch = newPassword === confirmPassword;

    // Set/clear mismatch error on confirmPassword control for UI display
    if (!passwordsMatch && confirmPasswordControl) {
      const existingErrors = confirmPasswordControl.errors || {};
      confirmPasswordControl.setErrors({ ...existingErrors, mismatch: 'Passwords do not match.' });
    } else if (passwordsMatch && confirmPasswordControl?.errors?.['mismatch']) {
      const { mismatch: _mismatch, ...otherErrors } = confirmPasswordControl.errors;
      confirmPasswordControl.setErrors(Object.keys(otherErrors).length > 0 ? otherErrors : null);
    }

    return passwordsMatch ? null : { mismatch: true };
  }
}
